import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

export default function ClientSnagProgressPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const { data } = await axios.get(`${API}/projects/${token}/snag-progress`);
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: "Invalid or expired link.", data: null });
      }
    };
    run();
    const iv = setInterval(run, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [token]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-gray-400 flex items-center justify-center text-xs font-mono">
        Loading…
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-[#050505] text-gray-400 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#d4af37] mb-2">Interazzo</p>
          <p className="text-white">{state.error}</p>
        </div>
      </div>
    );
  }

  const d = state.data;
  const pct = d.summary?.completion_percent ?? 0;
  const allDone = d.summary?.all_verified;

  return (
    <div className="min-h-screen bg-[#050505] text-gray-200 iz-grain">
      <header className="border-b border-[#262626] px-6 md:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/branding/interazzo-logo.png" alt="Interazzo" className="w-40 h-auto object-contain" />
          <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Client Viewport</p>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Read-only · auto refresh</span>
      </header>

      <main className="px-6 md:px-12 py-10 md:py-16 max-w-3xl mx-auto">
        <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-gray-500">Snag Resolution Progress</p>
        <h1 className="font-display text-3xl md:text-5xl text-white mt-2">{d.client_name}{d.city ? ` · ${d.city}` : ""}</h1>
        <p className="text-sm text-gray-500 mt-1">{d.project_name}</p>

        {/* Gauge */}
        <div className="mt-10 border border-[#262626] bg-[#0a0a0a] rounded-md p-8">
          <div className="flex items-baseline justify-between mb-3">
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">Collective resolution</span>
            <span className="font-display text-4xl md:text-6xl text-[#d4af37]">{pct}%</span>
          </div>
          <div className="w-full h-3 rounded-full bg-[#1c1c1c] overflow-hidden">
            <div
              className="h-full transition-all duration-700"
              style={{
                width: `${pct}%`,
                background: "linear-gradient(90deg, #d4af37 0%, #f5d67e 60%, #ffe9a8 100%)",
                boxShadow: "0 0 20px rgba(212,175,55,0.4)",
              }}
            />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Verified</p>
              <p className="text-xl text-emerald-300 font-mono">{d.summary?.verified ?? 0}</p>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">In progress</p>
              <p className="text-xl text-[#d4af37] font-mono">{d.summary?.resolved ?? 0}</p>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Open</p>
              <p className="text-xl text-gray-300 font-mono">{d.summary?.open ?? 0}</p>
            </div>
          </div>
        </div>

        {/* Categories */}
        {d.categories && Object.keys(d.categories).length > 0 && (
          <div className="mt-8">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-3">By category</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {Object.entries(d.categories).map(([cat, c]) => {
                const p = c.total ? Math.round((c.verified / c.total) * 100) : 0;
                return (
                  <div key={cat} className="border border-[#262626] bg-[#0a0a0a] rounded-md p-4">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">{cat}</p>
                    <p className="text-2xl text-white font-mono mt-1">{c.verified}/{c.total}</p>
                    <div className="w-full h-1.5 rounded-full bg-[#1c1c1c] overflow-hidden mt-2">
                      <div className="h-full bg-[#d4af37]" style={{ width: `${p}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {allDone && (
          <div className="mt-10 border border-[#d4af37]/40 bg-[#d4af37]/5 rounded-md p-6 flex items-start gap-4" data-testid="all-verified-banner">
            <CheckCircle2 className="w-6 h-6 text-[#d4af37] mt-0.5" strokeWidth={1.5} />
            <div>
              <p className="text-[#d4af37] font-display text-lg">All snags verified & closed.</p>
              <p className="text-xs text-gray-400 mt-1">
                The final settlement statement has been dispatched to your inbox.
              </p>
            </div>
          </div>
        )}

        {!allDone && (
          <p className="mt-10 text-[11px] text-gray-500 flex items-center gap-2">
            <Sparkles className="w-3 h-3 text-[#d4af37]" strokeWidth={1.5} />
            This page updates automatically every 20 seconds.
          </p>
        )}
      </main>
    </div>
  );
}
